import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Trophy, Medal, Award, Target, Users, Calendar, Plus, Edit,
  Trash2, Loader2, Play, CheckCircle, XCircle, Flag, Timer,
  Star, Crown, Swords, Shield, Flame, Search, User, School,
  CalendarDays, MapPin, X, Sparkles, GitBranch, LayoutGrid,
  ArrowRight, Goal, TrendingUp, Save, Eye, Printer, Download, Mail, FileText
} from 'lucide-react';
import api from '../lib/api';

// Modalidades de competição
const MODALIDADES = [
  { id: 'futebol', nome: 'Futebol', icon: '⚽', cor: 'bg-green-500', temGols: true },
  { id: 'futsal', nome: 'Futsal', icon: '⚽', cor: 'bg-green-600', temGols: true },
  { id: 'volei', nome: 'Vôlei', icon: '🏐', cor: 'bg-yellow-500', temGols: false },
  { id: 'basquete', nome: 'Basquete', icon: '🏀', cor: 'bg-orange-500', temGols: true },
  { id: 'handebol', nome: 'Handebol', icon: '🤾', cor: 'bg-blue-500', temGols: true },
  { id: 'queimada', nome: 'Queimada', icon: '🔴', cor: 'bg-red-500', temGols: false },
  { id: 'atletismo', nome: 'Atletismo', icon: '🏃', cor: 'bg-purple-500', temGols: false },
  { id: 'xadrez', nome: 'Xadrez', icon: '♟️', cor: 'bg-slate-700', temGols: false },
  { id: 'danca', nome: 'Dança', icon: '💃', cor: 'bg-fuchsia-500', temGols: false },
  { id: 'musica', nome: 'Música', icon: '🎵', cor: 'bg-indigo-500', temGols: false },
];

// Gêneros de competição
const GENEROS_COMPETICAO = [
  { id: 'masculino', nome: 'Masculino', icon: '♂️', cor: 'text-blue-600' },
  { id: 'feminino', nome: 'Feminino', icon: '♀️', cor: 'text-pink-600' },
  { id: 'misto', nome: 'Misto', icon: '⚥', cor: 'text-purple-600' },
];

// Tipos de competição
const TIPOS_COMPETICAO = [
  { id: 'pontos_corridos', nome: 'Pontos Corridos', desc: 'Todos jogam contra todos' },
  { id: 'grupos_matamata', nome: 'Grupos + Mata-Mata', desc: '2 grupos, semifinal e final' },
  { id: 'eliminatorias', nome: 'Eliminatórias', desc: 'Mata-mata direto' },
];

const Interclasse = () => {
  const [activeTab, setActiveTab] = useState('competicoes');
  const [turmas, setTurmas] = useState([]);
  const [alunosPorTurma, setAlunosPorTurma] = useState({});
  const [competicoes, setCompeticoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPartidaModal, setShowPartidaModal] = useState(false);
  const [showDetalhesModal, setShowDetalhesModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [gerandoSorteio, setGerandoSorteio] = useState(false);
  const [buscaAluno, setBuscaAluno] = useState('');
  const [competicaoSelecionada, setCompeticaoSelecionada] = useState(null);
  const [partidaEditando, setPartidaEditando] = useState(null);
  const [schoolLogo, setSchoolLogo] = useState(null);
  // Estados para impressão
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printType, setPrintType] = useState('partidas'); // partidas, classificacao, artilheiros
  const printRef = useRef(null);

  // Form de competição
  const [formData, setFormData] = useState({
    nome: '',
    modalidade: '',
    genero: 'misto', // masculino, feminino, misto
    tipo_competicao: 'pontos_corridos',
    data_inicio: '',
    data_fim: '',
    descricao: '',
    local: '',
    equipes: [], // { id, nome, turma_id, alunos: [] }
    status: 'planejado',
  });

  // Form de partida
  const [partidaForm, setPartidaForm] = useState({
    equipe1_id: '',
    equipe2_id: '',
    placar1: 0,
    placar2: 0,
    gols_equipe1: [], // { aluno_id, quantidade }
    gols_equipe2: [],
    data: '',
    horario: '', // Horário da partida
    fase: 'grupo', // grupo, semifinal, final
    grupo: 'A',
    status: 'agendada', // agendada, em_andamento, finalizada
  });

  // Carregar dados
  useEffect(() => {
    loadData();
    loadSchoolLogo();
  }, []);

  const loadSchoolLogo = async () => {
    try {
      const res = await api.get('/settings/school-logo');
      if (res.data?.logo_url) {
        setSchoolLogo(res.data.logo_url);
      }
    } catch (error) {
      console.log('Logo não encontrado');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('logo', file);  // Backend espera 'logo', não 'file'
    
    try {
      const res = await api.post('/settings/school-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSchoolLogo(res.data.logo_url);
      toast.success('Logo atualizado!');
    } catch (error) {
      toast.error('Erro ao fazer upload do logo');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [turmasRes, competicoesRes] = await Promise.all([
        api.get('/class-groups'),
        api.get('/interclasse/competicoes'),
      ]);
      
      const turmasOrdenadas = (turmasRes.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setTurmas(turmasOrdenadas);
      setCompeticoes(competicoesRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      // Fallback para localStorage se API falhar
      const saved = localStorage.getItem('interclasse_v2');
      if (saved) {
        setCompeticoes(JSON.parse(saved));
      }
    } finally {
      setLoading(false);
    }
  };

  const carregarAlunosTurma = async (turmaId) => {
    if (alunosPorTurma[turmaId]) return alunosPorTurma[turmaId];
    
    try {
      const response = await api.get(`/students?class_id=${turmaId}`);
      const alunos = (response.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setAlunosPorTurma(prev => ({ ...prev, [turmaId]: alunos }));
      return alunos;
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
      return [];
    }
  };

  const abrirNovoModal = () => {
    setEditingId(null);
    setFormData({
      nome: '',
      modalidade: '',
      genero: 'misto',
      tipo_competicao: 'pontos_corridos',
      data_inicio: '',
      data_fim: '',
      descricao: '',
      local: '',
      equipes: [],
      status: 'planejado',
    });
    setShowModal(true);
  };

  const abrirEditarModal = (comp) => {
    setEditingId(comp.id);
    setFormData({
      nome: comp.nome,
      modalidade: comp.modalidade,
      genero: comp.genero || 'misto',
      tipo_competicao: comp.tipo_competicao || 'pontos_corridos',
      data_inicio: comp.data_inicio || '',
      data_fim: comp.data_fim || '',
      descricao: comp.descricao || '',
      local: comp.local || '',
      equipes: comp.equipes || [],
      status: comp.status,
    });
    setShowModal(true);
  };

  const adicionarEquipe = () => {
    const novaEquipe = {
      id: Date.now().toString(),
      nome: `Equipe ${formData.equipes.length + 1}`,
      turma_id: '',
      alunos: [],
    };
    setFormData(prev => ({
      ...prev,
      equipes: [...prev.equipes, novaEquipe]
    }));
  };

  const removerEquipe = (equipeId) => {
    setFormData(prev => ({
      ...prev,
      equipes: prev.equipes.filter(e => e.id !== equipeId)
    }));
  };

  const atualizarEquipe = (equipeId, campo, valor) => {
    setFormData(prev => ({
      ...prev,
      equipes: prev.equipes.map(e => 
        e.id === equipeId ? { ...e, [campo]: valor } : e
      )
    }));
  };

  const toggleAlunoEquipe = (equipeId, alunoId) => {
    setFormData(prev => ({
      ...prev,
      equipes: prev.equipes.map(e => {
        if (e.id !== equipeId) return e;
        const alunos = e.alunos.includes(alunoId)
          ? e.alunos.filter(id => id !== alunoId)
          : [...e.alunos, alunoId];
        return { ...e, alunos };
      })
    }));
  };

  const gerarSorteioIA = async () => {
    if (formData.equipes.length < 2) {
      toast.error('Adicione pelo menos 2 equipes');
      return;
    }

    setGerandoSorteio(true);
    try {
      const equipesInfo = formData.equipes.map(e => ({
        id: e.id,
        nome: e.nome,
        turma: turmas.find(t => t.id === e.turma_id)?.name || e.nome,
        jogadores: e.alunos.length
      }));

      const tipoDesc = formData.tipo_competicao === 'grupos_matamata' 
        ? 'Divida em 2 grupos equilibrados (Grupo A e Grupo B). Os 2 melhores de cada grupo vão para semifinal.'
        : formData.tipo_competicao === 'eliminatorias'
        ? 'Sorteie os confrontos de eliminatórias diretas.'
        : 'Gere a tabela de jogos todos contra todos.';

      const response = await api.post('/ai/generate', {
        system_prompt: `Você é um organizador de competições esportivas escolares.
Gere o sorteio para uma competição de ${getModalidadeInfo(formData.modalidade).nome}.
Tipo: ${TIPOS_COMPETICAO.find(t => t.id === formData.tipo_competicao)?.nome}

${tipoDesc}

Equipes participantes:
${equipesInfo.map((e, i) => `${i+1}. ${e.nome} (${e.turma}) - ${e.jogadores} jogadores`).join('\n')}

Retorne APENAS um JSON válido no formato:
{
  "grupos": {
    "A": ["id_equipe1", "id_equipe2", ...],
    "B": ["id_equipe3", "id_equipe4", ...]
  },
  "partidas": [
    {
      "equipe1_id": "id",
      "equipe2_id": "id",
      "fase": "grupo|semifinal|final",
      "grupo": "A|B|null",
      "rodada": 1
    }
  ],
  "explicacao": "Breve explicação do sorteio"
}

IMPORTANTE: Use os IDs exatos das equipes: ${equipesInfo.map(e => e.id).join(', ')}`,
        user_prompt: 'Gere o sorteio agora.'
      });

      const content = response.data.content || '';
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const sorteio = JSON.parse(jsonMatch[0]);
        
        // Atualizar equipes com grupos
        if (sorteio.grupos) {
          const equipesAtualizadas = formData.equipes.map(e => ({
            ...e,
            grupo: Object.entries(sorteio.grupos).find(([g, ids]) => 
              ids.includes(e.id)
            )?.[0] || null
          }));
          setFormData(prev => ({ ...prev, equipes: equipesAtualizadas }));
        }

        // Salvar partidas geradas
        if (sorteio.partidas) {
          const partidasFormatadas = sorteio.partidas.map((p, idx) => ({
            id: `partida_${Date.now()}_${idx}`,
            ...p,
            placar1: 0,
            placar2: 0,
            gols_equipe1: [],
            gols_equipe2: [],
            status: 'agendada',
            data: ''
          }));
          setFormData(prev => ({ ...prev, partidas: partidasFormatadas }));
        }

        toast.success('Sorteio gerado com sucesso!');
        if (sorteio.explicacao) {
          toast.info(sorteio.explicacao, { duration: 5000 });
        }
      }
    } catch (error) {
      console.error('Erro ao gerar sorteio:', error);
      toast.error('Erro ao gerar sorteio');
    } finally {
      setGerandoSorteio(false);
    }
  };

  const salvarCompeticao = async () => {
    if (!formData.nome || !formData.modalidade) {
      toast.error('Preencha nome e modalidade');
      return;
    }

    setSalvando(true);
    try {
      if (editingId) {
        // Atualizar competição existente
        const response = await api.put(`/interclasse/competicoes/${editingId}`, formData);
        setCompeticoes(prev => prev.map(c => c.id === editingId ? response.data : c));
        toast.success('Competição atualizada!');
      } else {
        // Criar nova competição
        const response = await api.post('/interclasse/competicoes', {
          ...formData,
          partidas: formData.partidas || [],
        });
        setCompeticoes(prev => [...prev, response.data]);
        toast.success('Competição criada!');
      }
      setShowModal(false);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSalvando(false);
    }
  };

  const excluirCompeticao = async (id) => {
    if (!confirm('Excluir esta competição?')) return;
    
    try {
      await api.delete(`/interclasse/competicoes/${id}`);
      setCompeticoes(prev => prev.filter(c => c.id !== id));
      toast.success('Excluída!');
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir');
    }
  };

  const abrirDetalhes = (comp) => {
    setCompeticaoSelecionada(comp);
    setShowDetalhesModal(true);
  };

  const abrirNovaPartida = (comp) => {
    setCompeticaoSelecionada(comp);
    setPartidaEditando(null);
    setPartidaForm({
      equipe1_id: '',
      equipe2_id: '',
      placar1: 0,
      placar2: 0,
      gols_equipe1: [],
      gols_equipe2: [],
      data: '',
      horario: '',
      fase: 'grupo',
      grupo: 'A',
      status: 'agendada',
    });
    setShowPartidaModal(true);
  };

  const editarPartida = (comp, partida) => {
    setCompeticaoSelecionada(comp);
    setPartidaEditando(partida.id);
    setPartidaForm({
      ...partida,
      horario: partida.horario || '',
      gols_equipe1: partida.gols_equipe1 || [],
      gols_equipe2: partida.gols_equipe2 || [],
    });
    setShowPartidaModal(true);
  };

  const salvarPartida = async () => {
    if (!partidaForm.equipe1_id || !partidaForm.equipe2_id) {
      toast.error('Selecione as duas equipes');
      return;
    }

    try {
      // Verificar se há competição selecionada
      if (!competicaoSelecionada) {
        toast.error('Nenhuma competição selecionada');
        return;
      }

      // Preparar dados da partida
      const partidaData = {
        ...partidaForm,
        id: partidaEditando || Date.now().toString(),
      };

      // Calcular partidas atualizadas localmente primeiro
      let partidasAtualizadas = competicaoSelecionada.partidas || [];
      
      if (partidaEditando) {
        partidasAtualizadas = partidasAtualizadas.map(p => 
          p.id === partidaEditando ? partidaData : p
        );
      } else {
        partidasAtualizadas = [...partidasAtualizadas, partidaData];
      }

      // Atualizar no backend
      const response = await api.put(`/interclasse/competicoes/${competicaoSelecionada.id}`, {
        partidas: partidasAtualizadas,
      });

      // Atualizar estado local
      setCompeticoes(prev => prev.map(c => 
        c.id === competicaoSelecionada.id ? response.data : c
      ));
      setCompeticaoSelecionada(response.data);
      setShowPartidaModal(false);
      toast.success(partidaEditando ? 'Partida atualizada!' : 'Partida adicionada!');
    } catch (error) {
      console.error('Erro ao salvar partida:', error);
      toast.error('Erro ao salvar partida');
    }
  };

  const adicionarGol = (equipe, alunoId) => {
    const campo = equipe === 1 ? 'gols_equipe1' : 'gols_equipe2';
    setPartidaForm(prev => {
      const gols = [...prev[campo]];
      const existente = gols.find(g => g.aluno_id === alunoId);
      if (existente) {
        existente.quantidade++;
      } else {
        gols.push({ aluno_id: alunoId, quantidade: 1 });
      }
      return { ...prev, [campo]: gols };
    });
  };

  const removerGol = (equipe, alunoId) => {
    const campo = equipe === 1 ? 'gols_equipe1' : 'gols_equipe2';
    setPartidaForm(prev => {
      const gols = prev[campo].map(g => {
        if (g.aluno_id !== alunoId) return g;
        return { ...g, quantidade: Math.max(0, g.quantidade - 1) };
      }).filter(g => g.quantidade > 0);
      return { ...prev, [campo]: gols };
    });
  };

  const calcularArtilharia = (partidas, equipes) => {
    const gols = {};
    
    partidas.forEach(p => {
      [...(p.gols_equipe1 || []), ...(p.gols_equipe2 || [])].forEach(g => {
        if (!gols[g.aluno_id]) {
          gols[g.aluno_id] = { aluno_id: g.aluno_id, gols: 0 };
        }
        gols[g.aluno_id].gols += g.quantidade;
      });
    });

    return Object.values(gols).sort((a, b) => b.gols - a.gols);
  };

  const calcularClassificacao = (comp) => {
    if (!comp.equipes || !comp.partidas) return [];

    const tabela = {};
    
    comp.equipes.forEach(e => {
      tabela[e.id] = {
        equipe_id: e.id,
        nome: e.nome,
        grupo: e.grupo || '-',
        pontos: 0,
        jogos: 0,
        vitorias: 0,
        empates: 0,
        derrotas: 0,
        gols_pro: 0,
        gols_contra: 0,
        saldo: 0,
      };
    });

    comp.partidas.filter(p => p.status === 'finalizada').forEach(p => {
      if (!tabela[p.equipe1_id] || !tabela[p.equipe2_id]) return;

      const p1 = parseInt(p.placar1) || 0;
      const p2 = parseInt(p.placar2) || 0;

      tabela[p.equipe1_id].jogos++;
      tabela[p.equipe2_id].jogos++;
      tabela[p.equipe1_id].gols_pro += p1;
      tabela[p.equipe1_id].gols_contra += p2;
      tabela[p.equipe2_id].gols_pro += p2;
      tabela[p.equipe2_id].gols_contra += p1;

      if (p1 > p2) {
        tabela[p.equipe1_id].vitorias++;
        tabela[p.equipe1_id].pontos += 3;
        tabela[p.equipe2_id].derrotas++;
      } else if (p2 > p1) {
        tabela[p.equipe2_id].vitorias++;
        tabela[p.equipe2_id].pontos += 3;
        tabela[p.equipe1_id].derrotas++;
      } else {
        tabela[p.equipe1_id].empates++;
        tabela[p.equipe2_id].empates++;
        tabela[p.equipe1_id].pontos++;
        tabela[p.equipe2_id].pontos++;
      }

      tabela[p.equipe1_id].saldo = tabela[p.equipe1_id].gols_pro - tabela[p.equipe1_id].gols_contra;
      tabela[p.equipe2_id].saldo = tabela[p.equipe2_id].gols_pro - tabela[p.equipe2_id].gols_contra;
    });

    return Object.values(tabela).sort((a, b) => {
      if (b.pontos !== a.pontos) return b.pontos - a.pontos;
      if (b.vitorias !== a.vitorias) return b.vitorias - a.vitorias;
      if (b.saldo !== a.saldo) return b.saldo - a.saldo;
      return b.gols_pro - a.gols_pro;
    });
  };

  // Calcular artilheiros da competição
  const calcularArtilheiros = (comp) => {
    if (!comp || !comp.partidas) return [];
    
    const artilheiros = {};
    
    comp.partidas.forEach(partida => {
      // Gols da equipe 1
      (partida.gols_equipe1 || []).forEach(gol => {
        const alunoId = gol.aluno_id;
        const quantidade = parseInt(gol.quantidade) || 1;
        const equipe = comp.equipes?.find(e => e.id === partida.equipe1_id);
        
        if (!artilheiros[alunoId]) {
          artilheiros[alunoId] = {
            aluno_id: alunoId,
            nome: getAlunoNome(alunoId),
            equipe: equipe?.nome || 'Equipe',
            gols: 0
          };
        }
        artilheiros[alunoId].gols += quantidade;
      });
      
      // Gols da equipe 2
      (partida.gols_equipe2 || []).forEach(gol => {
        const alunoId = gol.aluno_id;
        const quantidade = parseInt(gol.quantidade) || 1;
        const equipe = comp.equipes?.find(e => e.id === partida.equipe2_id);
        
        if (!artilheiros[alunoId]) {
          artilheiros[alunoId] = {
            aluno_id: alunoId,
            nome: getAlunoNome(alunoId),
            equipe: equipe?.nome || 'Equipe',
            gols: 0
          };
        }
        artilheiros[alunoId].gols += quantidade;
      });
    });
    
    return Object.values(artilheiros)
      .filter(a => a.gols > 0)
      .sort((a, b) => b.gols - a.gols);
  };

  const getModalidadeInfo = (id) => MODALIDADES.find(m => m.id === id) || MODALIDADES[0];
  
  const getEquipeNome = (comp, equipeId) => {
    if (!comp || !comp.equipes) return 'Equipe';
    return comp.equipes.find(e => e.id === equipeId)?.nome || 'Equipe';
  };
  
  const getAlunoNome = (alunoId) => {
    for (const alunos of Object.values(alunosPorTurma)) {
      const aluno = alunos.find(a => a.id === alunoId);
      if (aluno) return aluno.name;
    }
    return 'Jogador';
  };

  const getStatusBadge = (status) => {
    const configs = {
      planejado: { label: 'Planejado', color: 'bg-slate-100 text-slate-700' },
      em_andamento: { label: 'Em Andamento', color: 'bg-green-100 text-green-700' },
      finalizado: { label: 'Finalizado', color: 'bg-blue-100 text-blue-700' },
      agendada: { label: 'Agendada', color: 'bg-yellow-100 text-yellow-700' },
      finalizada: { label: 'Finalizada', color: 'bg-green-100 text-green-700' },
    };
    return configs[status] || configs.planejado;
  };

  // ============================================
  // FUNÇÕES DE IMPRESSÃO E EXPORTAÇÃO
  // ============================================

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    // Garantir que o logo seja incluído se existir
    const logoHtml = schoolLogo 
      ? `<img src="${schoolLogo}" alt="Logo" style="max-height: 80px; margin-bottom: 10px; display: block; margin-left: auto; margin-right: auto;" />`
      : '';

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Impressão - ${competicaoSelecionada?.nome || 'Interclasse'}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .print-header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #6B21A8; }
          .print-header img { max-height: 80px; margin-bottom: 10px; display: block; margin-left: auto; margin-right: auto; }
          .print-header h1 { font-size: 18pt; color: #6B21A8; margin: 0 0 5px 0; }
          .print-header h2 { font-size: 14pt; color: #333; margin: 0; font-weight: normal; }
          .print-header p { font-size: 10pt; color: #666; margin: 5px 0 0 0; }
          .print-table { width: 100%; border-collapse: collapse; font-size: 11pt; margin-top: 15px; }
          .print-table th, .print-table td { border: 1px solid #333; padding: 8px 12px; text-align: left; }
          .print-table th { background-color: #6B21A8; color: white; font-weight: bold; }
          .print-table tr:nth-child(even) { background-color: #f8f8f8; }
          .print-footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #ccc; font-size: 9pt; color: #666; text-align: center; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 9pt; }
          .badge-yellow { background: #FEF3C7; color: #92400E; }
          .badge-green { background: #D1FAE5; color: #065F46; }
          .badge-blue { background: #DBEAFE; color: #1E40AF; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="print-header">
          ${logoHtml}
          <h1>🏆 ${competicaoSelecionada?.nome || 'Copa Interclasse'}</h1>
          <h2>${MODALIDADES.find(m => m.id === competicaoSelecionada?.modalidade)?.nome || ''} 
          ${competicaoSelecionada?.genero ? ' - ' + (GENEROS_COMPETICAO.find(g => g.id === competicaoSelecionada?.genero)?.nome || '') : ''}</h2>
        </div>
        ${printContent.innerHTML.replace(/<div class="print-header[^>]*>[\s\S]*?<\/div>/, '')}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleDownloadPDF = async () => {
    // Usar a biblioteca html2canvas + jspdf ou simplesmente print to PDF
    toast.info('Para salvar como PDF, use Ctrl+P e selecione "Salvar como PDF"');
    handlePrint();
  };

  const handleSendEmail = async () => {
    toast.info('Funcionalidade de envio por e-mail em desenvolvimento');
    // TODO: Implementar envio por e-mail
  };

  // Gerar dados para impressão
  const getPartidasParaImpressao = () => {
    if (!competicaoSelecionada) return [];
    return (competicaoSelecionada.partidas || []).map(p => {
      const eq1 = competicaoSelecionada.equipes?.find(e => e.id === p.equipe1_id);
      const eq2 = competicaoSelecionada.equipes?.find(e => e.id === p.equipe2_id);
      const status = getStatusBadge(p.status);
      return {
        ...p,
        equipe1_nome: eq1?.nome || 'Equipe 1',
        equipe2_nome: eq2?.nome || 'Equipe 2',
        status_label: status.label,
        data_formatada: p.data ? new Date(p.data + 'T12:00:00').toLocaleDateString('pt-BR') : '-',
      };
    });
  };

  const getClassificacaoParaImpressao = () => {
    if (!competicaoSelecionada) return [];
    return calcularClassificacao(competicaoSelecionada).map((eq, idx) => ({
      ...eq,
      posicao: idx + 1,
    }));
  };

  const getArtilheirosParaImpressao = () => {
    if (!competicaoSelecionada) return [];
    return calcularArtilheiros(competicaoSelecionada);
  };

  const openPrintModal = (type) => {
    setPrintType(type);
    setShowPrintModal(true);
  };

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2" data-testid="interclasse-title">
            <Trophy className="w-5 h-5 sm:w-7 sm:h-7 text-yellow-500" />
            Interclasse
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Competições esportivas e culturais entre turmas
          </p>
        </div>
        
        <Button onClick={abrirNovoModal} className="gap-2 bg-purple-600 hover:bg-purple-700 w-full sm:w-auto" data-testid="btn-nova-competicao">
          <Plus className="w-4 h-4" />
          Nova Competição
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Trophy className="w-6 h-6 sm:w-8 sm:h-8 opacity-80" />
              <div>
                <p className="text-lg sm:text-2xl font-bold">{competicoes.length}</p>
                <p className="text-xs sm:text-sm opacity-80">Competições</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Play className="w-6 h-6 sm:w-8 sm:h-8 opacity-80" />
              <div>
                <p className="text-lg sm:text-2xl font-bold">{competicoes.filter(c => c.status === 'em_andamento').length}</p>
                <p className="text-xs sm:text-sm opacity-80">Em Andamento</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Swords className="w-6 h-6 sm:w-8 sm:h-8 opacity-80" />
              <div>
                <p className="text-lg sm:text-2xl font-bold">{competicoes.reduce((a, c) => a + (c.partidas?.length || 0), 0)}</p>
                <p className="text-xs sm:text-sm opacity-80">Partidas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Goal className="w-6 h-6 sm:w-8 sm:h-8 opacity-80" />
              <div>
                <p className="text-2xl font-bold">
                  {competicoes.reduce((a, c) => a + (c.partidas?.reduce((b, p) => 
                    b + (parseInt(p.placar1) || 0) + (parseInt(p.placar2) || 0), 0) || 0), 0)}
                </p>
                <p className="text-sm opacity-80">Gols</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="competicoes" className="gap-2"><Trophy className="w-4 h-4" />Competições</TabsTrigger>
          <TabsTrigger value="classificacao" className="gap-2"><Medal className="w-4 h-4" />Classificação</TabsTrigger>
          <TabsTrigger value="artilharia" className="gap-2"><Goal className="w-4 h-4" />Artilharia</TabsTrigger>
          <TabsTrigger value="modalidades" className="gap-2"><Flag className="w-4 h-4" />Modalidades</TabsTrigger>
        </TabsList>

        {/* Tab Competições */}
        <TabsContent value="competicoes" className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
            </div>
          ) : competicoes.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed">
              <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nenhuma competição cadastrada</p>
              <Button onClick={abrirNovoModal} variant="link" className="text-purple-600">
                Criar primeira competição
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {competicoes.map(comp => {
                const mod = getModalidadeInfo(comp.modalidade);
                const status = getStatusBadge(comp.status);
                const tipo = TIPOS_COMPETICAO.find(t => t.id === comp.tipo_competicao);
                const genero = GENEROS_COMPETICAO.find(g => g.id === comp.genero);
                
                return (
                  <Card key={comp.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className={`h-2 ${mod.cor}`} />
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{mod.icon}</span>
                          <div>
                            <CardTitle className="text-lg">{comp.nome}</CardTitle>
                            <p className="text-sm text-muted-foreground">
                              {mod.nome} 
                              {genero && <span className={`ml-1 ${genero.cor}`}>{genero.icon}</span>}
                            </p>
                          </div>
                        </div>
                        <Badge className={status.color}>{status.label}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex flex-wrap gap-2 text-sm">
                        {genero && (
                          <Badge variant="outline" className={`gap-1 ${genero.cor}`}>
                            <span>{genero.icon}</span>
                            {genero.nome}
                          </Badge>
                        )}
                        <Badge variant="outline" className="gap-1">
                          <LayoutGrid className="w-3 h-3" />
                          {tipo?.nome || 'Pontos Corridos'}
                        </Badge>
                        <Badge variant="outline" className="gap-1">
                          <Users className="w-3 h-3" />
                          {comp.equipes?.length || 0} equipes
                        </Badge>
                        <Badge variant="outline" className="gap-1">
                          <Swords className="w-3 h-3" />
                          {comp.partidas?.length || 0} partidas
                        </Badge>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => abrirDetalhes(comp)}>
                          <Eye className="w-4 h-4 mr-1" />Ver
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => abrirEditarModal(comp)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-600 hover:bg-red-50 hover:text-red-700" 
                          onClick={() => excluirCompeticao(comp.id)}
                          data-testid={`btn-excluir-${comp.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Tab Classificação */}
        <TabsContent value="classificacao" className="space-y-4">
          {competicoes.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed">
              <Medal className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Crie competições para ver a classificação</p>
            </div>
          ) : (
            competicoes.map(comp => {
              const classificacao = calcularClassificacao(comp);
              const mod = getModalidadeInfo(comp.modalidade);
              
              if (classificacao.length === 0) return null;
              
              return (
                <Card key={comp.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="text-2xl">{mod.icon}</span>
                      {comp.nome}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left py-2 px-2">#</th>
                            <th className="text-left py-2 px-2">Equipe</th>
                            {comp.tipo_competicao === 'grupos_matamata' && <th className="text-center py-2 px-2">Gr</th>}
                            <th className="text-center py-2 px-2">P</th>
                            <th className="text-center py-2 px-2">J</th>
                            <th className="text-center py-2 px-2">V</th>
                            <th className="text-center py-2 px-2">E</th>
                            <th className="text-center py-2 px-2">D</th>
                            <th className="text-center py-2 px-2">GP</th>
                            <th className="text-center py-2 px-2">GC</th>
                            <th className="text-center py-2 px-2">SG</th>
                          </tr>
                        </thead>
                        <tbody>
                          {classificacao.map((item, idx) => (
                            <tr key={item.equipe_id} className={`border-b ${idx < 2 ? 'bg-green-50' : idx < 4 ? 'bg-yellow-50' : ''}`}>
                              <td className="py-2 px-2">
                                {idx === 0 ? <Crown className="w-4 h-4 text-yellow-500" /> :
                                 idx === 1 ? <Medal className="w-4 h-4 text-slate-400" /> :
                                 idx === 2 ? <Award className="w-4 h-4 text-amber-600" /> : idx + 1}
                              </td>
                              <td className="py-2 px-2 font-medium">{item.nome}</td>
                              {comp.tipo_competicao === 'grupos_matamata' && <td className="text-center py-2 px-2">{item.grupo}</td>}
                              <td className="text-center py-2 px-2 font-bold text-purple-600">{item.pontos}</td>
                              <td className="text-center py-2 px-2">{item.jogos}</td>
                              <td className="text-center py-2 px-2 text-green-600">{item.vitorias}</td>
                              <td className="text-center py-2 px-2 text-yellow-600">{item.empates}</td>
                              <td className="text-center py-2 px-2 text-red-600">{item.derrotas}</td>
                              <td className="text-center py-2 px-2">{item.gols_pro}</td>
                              <td className="text-center py-2 px-2">{item.gols_contra}</td>
                              <td className="text-center py-2 px-2">{item.saldo > 0 ? '+' : ''}{item.saldo}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* Tab Artilharia */}
        <TabsContent value="artilharia" className="space-y-4">
          {competicoes.filter(c => c.artilharia?.length > 0).length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed">
              <Goal className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Registre gols nas partidas para ver a artilharia</p>
            </div>
          ) : (
            competicoes.filter(c => c.artilharia?.length > 0).map(comp => {
              const mod = getModalidadeInfo(comp.modalidade);
              
              return (
                <Card key={comp.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="text-2xl">{mod.icon}</span>
                      Artilharia - {comp.nome}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {comp.artilharia.slice(0, 10).map((art, idx) => (
                        <div key={art.aluno_id} className={`flex items-center gap-3 p-3 rounded-lg ${
                          idx === 0 ? 'bg-yellow-100' : idx < 3 ? 'bg-slate-100' : 'bg-slate-50'
                        }`}>
                          <span className="font-bold text-lg w-6">
                            {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                          </span>
                          <div className="flex-1">
                            <p className="font-medium">{getAlunoNome(art.aluno_id)}</p>
                          </div>
                          <Badge className="bg-purple-600 text-white">
                            {art.gols} gol{art.gols !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* Tab Modalidades */}
        <TabsContent value="modalidades">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {MODALIDADES.map(mod => (
              <Card key={mod.id} className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-4 text-center">
                  <div className={`w-14 h-14 ${mod.cor} rounded-full flex items-center justify-center mx-auto mb-3`}>
                    <span className="text-2xl">{mod.icon}</span>
                  </div>
                  <h3 className="font-semibold text-sm">{mod.nome}</h3>
                  <p className="text-xs text-muted-foreground">{competicoes.filter(c => c.modalidade === mod.id).length} comp.</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal Nova/Editar Competição */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingId ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingId ? 'Editar Competição' : 'Nova Competição'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-6 py-4">
            {/* Info básica */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input value={formData.nome} onChange={e => setFormData({...formData, nome: e.target.value})} placeholder="Interclasse 2026" />
              </div>
              <div className="space-y-2">
                <Label>Modalidade *</Label>
                <Select value={formData.modalidade} onValueChange={v => setFormData({...formData, modalidade: v})}>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {MODALIDADES.map(m => (
                      <SelectItem key={m.id} value={m.id}><span className="mr-2">{m.icon}</span>{m.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Gênero *</Label>
                <Select value={formData.genero} onValueChange={v => setFormData({...formData, genero: v})}>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {GENEROS_COMPETICAO.map(g => (
                      <SelectItem key={g.id} value={g.id}>
                        <span className={`mr-2 ${g.cor}`}>{g.icon}</span>{g.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo de Competição</Label>
                <Select value={formData.tipo_competicao} onValueChange={v => setFormData({...formData, tipo_competicao: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_COMPETICAO.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        <div>
                          <p className="font-medium">{t.nome}</p>
                          <p className="text-xs text-muted-foreground">{t.desc}</p>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Data Início</Label>
                <Input type="date" value={formData.data_inicio} onChange={e => setFormData({...formData, data_inicio: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Data Fim</Label>
                <Input type="date" value={formData.data_fim} onChange={e => setFormData({...formData, data_fim: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Local</Label>
                <Input value={formData.local} onChange={e => setFormData({...formData, local: e.target.value})} placeholder="Quadra" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={v => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planejado">Planejado</SelectItem>
                    <SelectItem value="em_andamento">Em Andamento</SelectItem>
                    <SelectItem value="finalizado">Finalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Equipes */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Equipes ({formData.equipes.length})
                </Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={adicionarEquipe}>
                    <Plus className="w-4 h-4 mr-1" />Equipe
                  </Button>
                  {formData.equipes.length >= 2 && (
                    <Button variant="outline" size="sm" onClick={gerarSorteioIA} disabled={gerandoSorteio} className="gap-1">
                      {gerandoSorteio ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      Sortear com IA
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-3 max-h-80 overflow-y-auto">
                {formData.equipes.map((equipe, idx) => (
                  <Card key={equipe.id} className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-2">
                          <Input 
                            value={equipe.nome} 
                            onChange={e => atualizarEquipe(equipe.id, 'nome', e.target.value)}
                            placeholder="Nome da equipe"
                            className="flex-1"
                          />
                          {equipe.grupo && <Badge variant="outline">Grupo {equipe.grupo}</Badge>}
                        </div>
                        
                        <div className="flex gap-2">
                          <Select 
                            value={equipe.turma_id} 
                            onValueChange={v => {
                              atualizarEquipe(equipe.id, 'turma_id', v);
                              carregarAlunosTurma(v);
                            }}
                          >
                            <SelectTrigger className="w-48"><SelectValue placeholder="Turma..." /></SelectTrigger>
                            <SelectContent>
                              {turmas.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          
                          <Badge variant="outline">{equipe.alunos.length} jogadores</Badge>
                        </div>

                        {equipe.turma_id && alunosPorTurma[equipe.turma_id] && (
                          <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto p-2 bg-slate-50 rounded">
                            {alunosPorTurma[equipe.turma_id].map(aluno => (
                              <Badge 
                                key={aluno.id}
                                variant={equipe.alunos.includes(aluno.id) ? "default" : "outline"}
                                className="cursor-pointer text-xs"
                                onClick={() => toggleAlunoEquipe(equipe.id, aluno.id)}
                              >
                                {aluno.name?.split(' ')[0]}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removerEquipe(equipe.id)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button onClick={salvarCompeticao} disabled={salvando} className="bg-purple-600 hover:bg-purple-700">
              {salvando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Detalhes/Partidas */}
      <Dialog open={showDetalhesModal} onOpenChange={setShowDetalhesModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              {competicaoSelecionada?.nome}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Botões de Impressão e Exportação */}
            <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg">
              <Button variant="outline" size="sm" onClick={() => openPrintModal('partidas')} className="gap-1">
                <FileText className="w-4 h-4" />Tabela de Partidas
              </Button>
              <Button variant="outline" size="sm" onClick={() => openPrintModal('classificacao')} className="gap-1">
                <Trophy className="w-4 h-4" />Classificação
              </Button>
              <Button variant="outline" size="sm" onClick={() => openPrintModal('artilheiros')} className="gap-1">
                <Goal className="w-4 h-4" />Artilheiros
              </Button>
              <Button variant="outline" size="sm" onClick={() => openPrintModal('tudo')} className="gap-1 bg-purple-50">
                <Printer className="w-4 h-4" />Imprimir Tudo
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Swords className="w-4 h-4" />
                Partidas ({competicaoSelecionada?.partidas?.length || 0})
              </h3>
              <Button size="sm" onClick={() => abrirNovaPartida(competicaoSelecionada)} className="gap-1">
                <Plus className="w-4 h-4" />Nova Partida
              </Button>
            </div>

            {competicaoSelecionada?.partidas?.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg">
                <Swords className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-muted-foreground">Nenhuma partida registrada</p>
              </div>
            ) : (
              <div className="space-y-2">
                {competicaoSelecionada?.partidas?.map(partida => {
                  const eq1 = competicaoSelecionada?.equipes?.find(e => e.id === partida.equipe1_id);
                  const eq2 = competicaoSelecionada?.equipes?.find(e => e.id === partida.equipe2_id);
                  const status = getStatusBadge(partida.status);
                  
                  return (
                    <Card key={partida.id} className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-1 text-center">
                          <p className="font-semibold">{eq1?.nome || 'Equipe 1'}</p>
                          {partida.gols_equipe1?.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {partida.gols_equipe1.map(g => `${getAlunoNome(g.aluno_id)} (${g.quantidade})`).join(', ')}
                            </p>
                          )}
                        </div>
                        
                        <div className="text-center px-4">
                          <div className="text-2xl font-bold">
                            {partida.placar1} <span className="text-muted-foreground mx-1">x</span> {partida.placar2}
                          </div>
                          <Badge className={`${status.color} text-xs`}>{status.label}</Badge>
                          {/* Data e Horário */}
                          {(partida.data || partida.horario) && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {partida.data && new Date(partida.data + 'T12:00:00').toLocaleDateString('pt-BR')}
                              {partida.data && partida.horario && ' às '}
                              {partida.horario}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex-1 text-center">
                          <p className="font-semibold">{eq2?.nome || 'Equipe 2'}</p>
                          {partida.gols_equipe2?.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {partida.gols_equipe2.map(g => `${getAlunoNome(g.aluno_id)} (${g.quantidade})`).join(', ')}
                            </p>
                          )}
                        </div>

                        <Button variant="outline" size="sm" onClick={() => editarPartida(competicaoSelecionada, partida)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Registrar/Editar Partida */}
      {competicaoSelecionada && (
        <Dialog open={showPartidaModal} onOpenChange={setShowPartidaModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Swords className="w-5 h-5 text-purple-600" />
                {partidaEditando ? 'Editar Placar da Partida' : 'Registrar Nova Partida'}
              </DialogTitle>
              <DialogDescription>
                Selecione as equipes e registre o placar com os gols de cada jogador
              </DialogDescription>
            </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Seleção de Equipes */}
            <div className="grid grid-cols-5 gap-2 items-center">
              <div className="col-span-2 space-y-2">
                <Label className="text-blue-600 font-semibold">Equipe Casa</Label>
                <Select value={partidaForm.equipe1_id} onValueChange={v => setPartidaForm({...partidaForm, equipe1_id: v})}>
                  <SelectTrigger className="border-blue-200 bg-blue-50/50"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {competicaoSelecionada?.equipes?.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.nome} ({e.alunos?.length || 0} jogadores)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-center">
                <span className="text-2xl font-bold text-gray-400">VS</span>
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="text-red-600 font-semibold">Equipe Visitante</Label>
                <Select value={partidaForm.equipe2_id} onValueChange={v => setPartidaForm({...partidaForm, equipe2_id: v})}>
                  <SelectTrigger className="border-red-200 bg-red-50/50"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {competicaoSelecionada?.equipes?.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.nome} ({e.alunos?.length || 0} jogadores)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Placar Principal */}
            <div className="bg-gradient-to-r from-blue-50 via-gray-50 to-red-50 rounded-xl p-4">
              <div className="grid grid-cols-5 gap-4 items-center">
                <div className="col-span-2 text-center">
                  <p className="text-sm font-medium text-blue-600 mb-2">
                    {getEquipeNome(competicaoSelecionada, partidaForm.equipe1_id) || 'Equipe 1'}
                  </p>
                  <Input 
                    type="number" 
                    min="0" 
                    value={partidaForm.placar1} 
                    onChange={e => setPartidaForm({...partidaForm, placar1: parseInt(e.target.value) || 0})} 
                    className="text-4xl font-bold text-center h-16 text-blue-600 border-blue-200"
                  />
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-400 mb-2">PLACAR</p>
                  <span className="text-3xl font-bold text-gray-400">×</span>
                </div>
                <div className="col-span-2 text-center">
                  <p className="text-sm font-medium text-red-600 mb-2">
                    {getEquipeNome(competicaoSelecionada, partidaForm.equipe2_id) || 'Equipe 2'}
                  </p>
                  <Input 
                    type="number" 
                    min="0" 
                    value={partidaForm.placar2} 
                    onChange={e => setPartidaForm({...partidaForm, placar2: parseInt(e.target.value) || 0})} 
                    className="text-4xl font-bold text-center h-16 text-red-600 border-red-200"
                  />
                </div>
              </div>
            </div>

            {/* Gols por Jogador - EQUIPE 1 */}
            {getModalidadeInfo(competicaoSelecionada?.modalidade)?.temGols && partidaForm.equipe1_id && (
              <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <Goal className="w-5 h-5 text-blue-600" />
                  <Label className="font-semibold text-blue-800">
                    Gols - {getEquipeNome(competicaoSelecionada, partidaForm.equipe1_id)}
                  </Label>
                  <Badge variant="outline" className="ml-auto bg-blue-100 text-blue-700">
                    {partidaForm.gols_equipe1.reduce((acc, g) => acc + g.quantidade, 0)} gols
                  </Badge>
                </div>
                
                {competicaoSelecionada?.equipes?.find(e => e.id === partidaForm.equipe1_id)?.alunos?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {competicaoSelecionada?.equipes?.find(e => e.id === partidaForm.equipe1_id)?.alunos?.map(alunoId => {
                      const gols = partidaForm.gols_equipe1.find(g => g.aluno_id === alunoId)?.quantidade || 0;
                      return (
                        <div key={alunoId} className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
                          gols > 0 ? 'bg-blue-100 border-blue-300' : 'bg-white border-gray-200'
                        }`}>
                          <User className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="text-sm font-medium flex-1 truncate">{getAlunoNome(alunoId)}</span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 w-7 p-0 hover:bg-red-100" 
                              onClick={() => removerGol(1, alunoId)}
                              disabled={gols === 0}
                            >-</Button>
                            <span className="w-6 text-center font-bold text-blue-600">{gols}</span>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 w-7 p-0 hover:bg-green-100" 
                              onClick={() => adicionarGol(1, alunoId)}
                            >+</Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-blue-600/60 text-center py-4">
                    Nenhum jogador cadastrado nesta equipe. Adicione jogadores na configuração da competição.
                  </p>
                )}
              </div>
            )}

            {/* Gols por Jogador - EQUIPE 2 */}
            {getModalidadeInfo(competicaoSelecionada?.modalidade)?.temGols && partidaForm.equipe2_id && (
              <div className="bg-red-50/50 rounded-xl p-4 border border-red-200">
                <div className="flex items-center gap-2 mb-3">
                  <Goal className="w-5 h-5 text-red-600" />
                  <Label className="font-semibold text-red-800">
                    Gols - {getEquipeNome(competicaoSelecionada, partidaForm.equipe2_id)}
                  </Label>
                  <Badge variant="outline" className="ml-auto bg-red-100 text-red-700">
                    {partidaForm.gols_equipe2.reduce((acc, g) => acc + g.quantidade, 0)} gols
                  </Badge>
                </div>
                
                {competicaoSelecionada?.equipes?.find(e => e.id === partidaForm.equipe2_id)?.alunos?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {competicaoSelecionada?.equipes?.find(e => e.id === partidaForm.equipe2_id)?.alunos?.map(alunoId => {
                      const gols = partidaForm.gols_equipe2.find(g => g.aluno_id === alunoId)?.quantidade || 0;
                      return (
                        <div key={alunoId} className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
                          gols > 0 ? 'bg-red-100 border-red-300' : 'bg-white border-gray-200'
                        }`}>
                          <User className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <span className="text-sm font-medium flex-1 truncate">{getAlunoNome(alunoId)}</span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 w-7 p-0 hover:bg-red-100" 
                              onClick={() => removerGol(2, alunoId)}
                              disabled={gols === 0}
                            >-</Button>
                            <span className="w-6 text-center font-bold text-red-600">{gols}</span>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 w-7 p-0 hover:bg-green-100" 
                              onClick={() => adicionarGol(2, alunoId)}
                            >+</Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-red-600/60 text-center py-4">
                    Nenhum jogador cadastrado nesta equipe. Adicione jogadores na configuração da competição.
                  </p>
                )}
              </div>
            )}

            {/* Info adicional */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Data</Label>
                <Input type="date" value={partidaForm.data} onChange={e => setPartidaForm({...partidaForm, data: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Horário</Label>
                <Input type="time" value={partidaForm.horario || ''} onChange={e => setPartidaForm({...partidaForm, horario: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Fase</Label>
                <Select value={partidaForm.fase} onValueChange={v => setPartidaForm({...partidaForm, fase: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grupo">Fase de Grupos</SelectItem>
                    <SelectItem value="semifinal">Semifinal</SelectItem>
                    <SelectItem value="final">Final</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={partidaForm.status} onValueChange={v => setPartidaForm({...partidaForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agendada">Agendada</SelectItem>
                    <SelectItem value="em_andamento">Em Andamento</SelectItem>
                    <SelectItem value="finalizada">Finalizada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowPartidaModal(false)}>Cancelar</Button>
            <Button onClick={salvarPartida} className="bg-purple-600 hover:bg-purple-700">
              <Save className="w-4 h-4 mr-2" />Salvar Partida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* ============================================
          MODAL DE IMPRESSÃO - PARTIDAS/CLASSIFICAÇÃO/ARTILHEIROS
          ============================================ */}
      <Dialog open={showPrintModal} onOpenChange={setShowPrintModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-purple-600" />
              Imprimir Torneio
            </DialogTitle>
            <DialogDescription>
              Selecione o tipo de relatório e adicione seu logo se desejar
            </DialogDescription>
          </DialogHeader>

          {/* Seletor de tipo e upload de logo */}
          <div className="flex flex-wrap items-center gap-2 border-b pb-3">
            <Button 
              variant={printType === 'grupos' ? 'default' : 'outline'} 
              size="sm" 
              onClick={() => setPrintType('grupos')}
            >
              Grupos
            </Button>
            <Button 
              variant={printType === 'partidas' ? 'default' : 'outline'} 
              size="sm" 
              onClick={() => setPrintType('partidas')}
            >
              Partidas
            </Button>
            <Button 
              variant={printType === 'matamata' ? 'default' : 'outline'} 
              size="sm" 
              onClick={() => setPrintType('matamata')}
            >
              Mata-Mata
            </Button>
            <Button 
              variant={printType === 'artilheiros' ? 'default' : 'outline'} 
              size="sm" 
              onClick={() => setPrintType('artilheiros')}
            >
              Artilheiros
            </Button>
            
            {/* Separador e Upload de Logo */}
            <div className="ml-auto flex items-center gap-2">
              {schoolLogo ? (
                <div className="flex items-center gap-2 bg-green-50 px-3 py-1 rounded-lg border border-green-200">
                  <img src={schoolLogo} alt="Logo" className="h-8 w-auto object-contain" />
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={async () => {
                      try {
                        await api.delete('/settings/school-logo');
                        setSchoolLogo(null);
                        toast.success('Logo removido');
                      } catch (error) {
                        setSchoolLogo(null);
                      }
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <Input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoUpload}
                  />
                  <div className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-slate-50 transition-colors">
                    <Download className="w-4 h-4 rotate-180" />
                    Upload Logo
                  </div>
                </label>
              )}
            </div>
          </div>

          {/* Preview de impressão */}
          <div className="flex-1 overflow-y-auto">
            <div ref={printRef} className="print-preview p-6 bg-white">
              {/* Header com logo opcional */}
              <div className="print-header text-center mb-6 pb-4 border-b-2 border-purple-600">
                {schoolLogo && (
                  <img 
                    src={schoolLogo} 
                    alt="Logo" 
                    className="mx-auto mb-3 max-h-20 object-contain"
                    style={{maxHeight: '80px'}}
                  />
                )}
                <h1 className="text-xl font-bold text-purple-700">
                  🏆 {competicaoSelecionada?.nome || 'Copa DNA Interclasse 2026'}
                </h1>
                <h2 className="text-base text-gray-600 mt-1">
                  {MODALIDADES.find(m => m.id === competicaoSelecionada?.modalidade)?.nome} 
                  {competicaoSelecionada?.genero && ` - ${GENEROS_COMPETICAO.find(g => g.id === competicaoSelecionada?.genero)?.nome}`}
                </h2>
              </div>

              {/* TABELA DE GRUPOS */}
              {(printType === 'grupos' || printType === 'tudo') && (
                <div className="mb-6">
                  <h3 style={{fontSize: '14pt', fontWeight: 'bold', marginBottom: '10px', color: '#6B21A8'}}>
                    📊 GRUPOS
                  </h3>
                  <table className="print-table">
                    <thead>
                      <tr>
                        <th style={{width: '50px', textAlign: 'center'}}>#</th>
                        <th>Equipe</th>
                        <th style={{width: '50px', textAlign: 'center'}}>J</th>
                        <th style={{width: '50px', textAlign: 'center'}}>V</th>
                        <th style={{width: '50px', textAlign: 'center'}}>E</th>
                        <th style={{width: '50px', textAlign: 'center'}}>D</th>
                        <th style={{width: '50px', textAlign: 'center'}}>GP</th>
                        <th style={{width: '50px', textAlign: 'center'}}>GC</th>
                        <th style={{width: '50px', textAlign: 'center'}}>SG</th>
                        <th style={{width: '50px', textAlign: 'center'}}>Pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getClassificacaoParaImpressao().map((eq, idx) => (
                        <tr key={idx} style={idx < 2 ? {backgroundColor: '#D1FAE5'} : {}}>
                          <td style={{textAlign: 'center', fontWeight: 'bold'}}>{eq.posicao}º</td>
                          <td style={{fontWeight: idx < 2 ? 'bold' : 'normal'}}>{eq.nome}</td>
                          <td style={{textAlign: 'center'}}>{eq.jogos}</td>
                          <td style={{textAlign: 'center'}}>{eq.vitorias}</td>
                          <td style={{textAlign: 'center'}}>{eq.empates}</td>
                          <td style={{textAlign: 'center'}}>{eq.derrotas}</td>
                          <td style={{textAlign: 'center'}}>{eq.gols_pro}</td>
                          <td style={{textAlign: 'center'}}>{eq.gols_contra}</td>
                          <td style={{textAlign: 'center'}}>{eq.saldo}</td>
                          <td style={{textAlign: 'center', fontWeight: 'bold', fontSize: '12pt'}}>{eq.pontos}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* TABELA DE PARTIDAS */}
              {(printType === 'partidas' || printType === 'tudo') && (
                <div className="mb-6">
                  <h3 style={{fontSize: '14pt', fontWeight: 'bold', marginBottom: '10px', color: '#6B21A8'}}>
                    📋 PARTIDAS
                  </h3>
                  <table className="print-table">
                    <thead>
                      <tr>
                        <th style={{width: '80px'}}>Data</th>
                        <th style={{width: '60px'}}>Horário</th>
                        <th>Equipe Casa</th>
                        <th style={{width: '80px', textAlign: 'center'}}>Placar</th>
                        <th>Equipe Visitante</th>
                        <th style={{width: '80px'}}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPartidasParaImpressao().map((partida, idx) => (
                        <tr key={idx}>
                          <td>{partida.data_formatada}</td>
                          <td>{partida.horario || '-'}</td>
                          <td style={{fontWeight: partida.placar1 > partida.placar2 ? 'bold' : 'normal'}}>
                            {partida.equipe1_nome}
                          </td>
                          <td style={{textAlign: 'center', fontWeight: 'bold', fontSize: '14pt'}}>
                            {partida.placar1 ?? '-'} x {partida.placar2 ?? '-'}
                          </td>
                          <td style={{fontWeight: partida.placar2 > partida.placar1 ? 'bold' : 'normal'}}>
                            {partida.equipe2_nome}
                          </td>
                          <td>
                            <span className={`badge ${partida.status === 'finalizada' ? 'badge-green' : partida.status === 'em_andamento' ? 'badge-blue' : 'badge-yellow'}`}>
                              {partida.status_label}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* TABELA MATA-MATA */}
              {(printType === 'matamata' || printType === 'tudo') && (
                <div className="mb-6">
                  <h3 style={{fontSize: '14pt', fontWeight: 'bold', marginBottom: '10px', color: '#6B21A8'}}>
                    🏆 MATA-MATA
                  </h3>
                  <div className="text-center py-4 text-gray-500">
                    Fase eliminatória em breve...
                  </div>
                </div>
              )}

              {/* TABELA DE ARTILHEIROS */}
              {(printType === 'artilheiros' || printType === 'tudo') && (
                <div className="mb-6">
                  <h3 style={{fontSize: '14pt', fontWeight: 'bold', marginBottom: '10px', color: '#6B21A8'}}>
                    ⚽ ARTILHEIROS
                  </h3>
                  <table className="print-table">
                    <thead>
                      <tr>
                        <th style={{width: '50px', textAlign: 'center'}}>#</th>
                        <th>Jogador</th>
                        <th>Equipe</th>
                        <th style={{width: '80px', textAlign: 'center'}}>Gols</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getArtilheirosParaImpressao().slice(0, 15).map((art, idx) => (
                        <tr key={idx} style={idx === 0 ? {backgroundColor: '#FEF3C7'} : {}}>
                          <td style={{textAlign: 'center', fontWeight: 'bold'}}>
                            {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}º`}
                          </td>
                          <td style={{fontWeight: idx < 3 ? 'bold' : 'normal'}}>{art.nome}</td>
                          <td>{art.equipe}</td>
                          <td style={{textAlign: 'center', fontWeight: 'bold', fontSize: '14pt'}}>{art.gols}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Footer */}
              <div className="print-footer">
                <p>Impresso em {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')} | www.novodna.com.br | #muitasmentesumamissão</p>
              </div>
            </div>
          </div>

          {/* Botões de ação */}
          <DialogFooter className="border-t pt-4 gap-2">
            <Button variant="outline" onClick={() => setShowPrintModal(false)}>
              Fechar
            </Button>
            <Button variant="outline" onClick={handleSendEmail} className="gap-1">
              <Mail className="w-4 h-4" />Enviar E-mail
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF} className="gap-1">
              <Download className="w-4 h-4" />Salvar PDF
            </Button>
            <Button onClick={handlePrint} className="bg-purple-600 hover:bg-purple-700 gap-1">
              <Printer className="w-4 h-4" />Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Interclasse;
